/**
  Frontend API interface
*/

import { API_BASE_URL } from '../config.js';
import { getSessionToken } from '../lib/sessionToken.js';

/**
 * Fetch an API URL and attach the session token when one is stored.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
async function apiFetch(url, options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getSessionToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(url, { ...options, headers });
}


/**
 * Logs in a user by sending credentials to the server.
 * @async
 * @function login
 * @param {string} username Username or email address
 * @param {string} password
 * @returns {Promise<Object>} Auth data or error.
 */
export async function loginUser(username, password, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to login" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Request a password-reset email for a username or email.
 * @async
 * @param {string} identifier Username or email
 * @returns {Promise<{success?: boolean, message?: string, error?: string}>}
 */
export async function requestPasswordReset(identifier, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/password-reset/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to request password reset" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Check whether a password-reset token is still valid.
 * @async
 * @param {string} token Raw token from the email link
 * @returns {Promise<{success?: boolean, error?: string}>}
 */
export async function validatePasswordReset(token, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams({ token: token || "" });
    const res = await apiFetch(`${baseUrl}/api/password-reset/validate/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "This reset link is invalid or expired." };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Set a new password using a reset token.
 * @async
 * @param {string} token Raw token from the email link
 * @param {string} newPassword Replacement password
 * @returns {Promise<{success?: boolean, error?: string}>}
 */
export async function confirmPasswordReset(token, newPassword, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/password-reset/confirm/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to reset password" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Logs out one session.
 * Pass `token` to revoke a saved account other than the active bearer token.
 * @async
 * @function logout
 * @param {number} [userId]
 * @param {string} [baseUrl]
 * @param {string} [token] Raw session token to revoke
 * @returns {Promise<Object>} Result or error.
 */
export async function logoutUser(userId, baseUrl = API_BASE_URL, token) {
  try {
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await apiFetch(`${baseUrl}/api/logout/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to logout" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load the signed-in profile for a saved account without changing the active token.
 * @param {"user"|"organization"} accountType
 * @param {number} accountId
 * @param {string} token Raw session token for that account
 * @param {string} [baseUrl]
 * @returns {Promise<{data?: Object, error?: string, unauthorized?: boolean}>}
 */
export async function fetchOwnProfile(accountType, accountId, token, baseUrl = API_BASE_URL) {
  const id = Number(accountId);
  const path = accountType === "organization"
    ? `/api/organizations/${id}/info/${id}/`
    : `/api/users/${id}/info/${id}/`;
  try {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await apiFetch(`${baseUrl}${path}`, { headers });
    const data = await res.json().catch(() => ({}));
    if (res.status === 401 || res.status === 403) {
      return { error: data.error || "Authentication required", unauthorized: true };
    }
    if (!res.ok) return { error: data.error || "Failed to load account" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Check whether a username is available.
 * @async
 * @param {string} username
 * @param {number} [excludeId] Account id to ignore (the signed-in user editing their own name)
 * @param {string} [excludeType] ``user``, ``organization``, or ``admin``
 * @returns {Promise<{success?: boolean, available?: boolean, reason?: string, error?: string}>}
 */
export async function checkUsernameAvailable(
  username,
  excludeId,
  excludeType,
  baseUrl = API_BASE_URL
) {
  try {
    const params = new URLSearchParams({ username: username || "" });
    if (excludeId) {
      params.set("exclude_id", String(excludeId));
    }
    if (excludeType) {
      params.set("exclude_type", String(excludeType));
    }
    const res = await apiFetch(`${baseUrl}/api/usernames/available/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to check username" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Suggest a free username from a first and last name.
 * Uses the same rules as an individual invite.
 * @async
 * @param {string} firstName
 * @param {string} lastName
 * @returns {Promise<{success?: boolean, username?: string, error?: string}>}
 */
export async function suggestUsername(firstName, lastName, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams({
      first_name: firstName || "",
      last_name: lastName || "",
    });
    const res = await apiFetch(`${baseUrl}/api/usernames/suggest/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to suggest a username" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Creates a new user by sending a POST request to the server.
 *
 * @async
 * @function createUser
 * @param {Object} userData - The user data to be sent in the request body.
 * @returns {Promise<Object>} The created user data if successful, or an error object if failed.
 * @throws {Error} If a network error or server issue occurs.
 */
export async function createUser(userData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });

    if (!res.ok) {
      const errorData = await res.json();
      return { error: errorData.message || errorData.error || "Failed to create user" };
    }

    const data = await res.json();
    return data;

  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Public individual signup. Account stays pending until admin review.
 * @async
 * @param {Object} userData
 * @returns {Promise<Object>}
 */
export async function createIndividualSignup(userData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/signup/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || data.message || "Failed to create account" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function createInviteByEmail(inviteData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(inviteData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create invite" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function validateInviteToken(token, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/validate/?token=${encodeURIComponent(token)}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to validate invite" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function acceptInviteToken(token, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/accept/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to accept invite" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function signupFromInvite(payload, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/signup/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create account from invite" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationPendingInvites(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/organizations/${orgId}/pending/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load organization pending invites" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getUserPendingInvites(email, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/users/pending/?email=${encodeURIComponent(email)}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load user pending invites" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function cancelInviteToken(token, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/invites/cancel/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to cancel invite" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Deletes a user by ID.
 * @async
 * @function deleteUser
 * @param {number} userId - The ID of the user to delete.
 * @returns {Promise<Object>} Result of deletion or error.
 */
export async function deleteUser(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete user" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Changes a user or organization password.
 * @async
 * @function userChangePassword
 * @param {number} userId - The account ID.
 * @param {string} currentPassword - The current password.
 * @param {string} newPassword - The new password.
 * @returns {Promise<Object>} Result or error.
 */
export async function userChangePassword(
  userId,
  currentPassword,
  newPassword,
  baseUrl = API_BASE_URL
) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/change_password/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to change password" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Copy editable required user fields that a patch omitted.
 * User updates fail unless username, first_name, last_name, and email are present.
 * @param {Object} user - Current user record
 * @param {Object} patch - Fields to change
 * @returns {Object}
 */
export function withUserUpdateRequired(user, patch) {
  return fillOmittedRequired(user, ["username", "first_name", "last_name", "email"], patch);
}

/**
 * Copy editable required organization fields that a patch omitted.
 * Organization updates fail unless username, name, and email are present.
 * @param {Object} organization - Current organization record
 * @param {Object} patch - Fields to change
 * @returns {Object}
 */
export function withOrganizationUpdateRequired(organization, patch) {
  return fillOmittedRequired(organization, ["username", "name", "email"], patch);
}

/**
 * @param {Object} record
 * @param {string[]} keys
 * @param {Object} patch
 * @returns {Object}
 */
function fillOmittedRequired(record, keys, patch) {
  const filled = { ...patch };
  for (const key of keys) {
    if (filled[key] == null && typeof record?.[key] === "string") {
      filled[key] = record[key];
    }
  }
  return filled;
}

/**
 * Updates a user. Include every editable required field, or pass the
 * current user through withUserUpdateRequired first.
 * @async
 * @function updateUser
 * @param {number} userId - The user ID.
 * @param {Object} userData - User fields to update.
 * @returns {Promise<Object>} Result or error.
 */
export async function updateUser(userId, userData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update user" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets a user by ID.
 * @async
 * @function getUser
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} User data or error.
 */
export async function getUser(userId, targetUserId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/info/${targetUserId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get user" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets organizations where user is admin.
 * @async
 * @function getUserOrganizationsAdmin
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Organizations or error.
 */
export async function getUserOrganizationsAdmin(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/organizations/admin/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organizations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets organizations where user is member.
 * @async
 * @function getUserOrganizationsMember
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Organizations or error.
 */
export async function getUserOrganizationsMember(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/organizations/member/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organizations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets organizations where user is congregant.
 * @async
 * @function getUserOrganizationsCongregant
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Organizations or error.
 */
export async function getUserOrganizationsCongregant(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/organizations/congregant/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organizations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets posts by user.
 * @async
 * @function getUserPosts
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Posts or error.
 */
export async function getUserPosts(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/posts/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get posts" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets followers of a user.
 * @async
 * @function getUserFollowers
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Followers or error.
 */
export async function getUserFollowers(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/followers/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get followers" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets users followed by a user.
 * @async
 * @function getUserFollowing
 * @param {number} userId - The user ID.
 * @returns {Promise<Object>} Following or error.
 */
export async function getUserFollowing(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/following/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get following" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Organization Account

export async function createOrganization(orgData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(orgData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function deleteOrganization(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function updateOrganization(orgId, orgData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(orgData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganization(userId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/info/${userId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationChildren(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/children/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization children" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationPosts(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/posts/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization posts" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationAdmins(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/admins/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization admins" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationMembers(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/members/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization members" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationCongregants(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/congregants/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization congregants" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationFollowers(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/followers/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization followers" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getOrganizationFollowing(orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/following/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization following" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// User/Organization Affiliation

/**
 * Creates a new user/organization affiliation.
 * @async
 * @function createAffiliation
 * @param {Object} affiliationData - The affiliation data.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Affiliation creation result or error.
 */
export async function createAffiliation(affiliationData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/affiliations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(affiliationData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create affiliation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Updates an existing user/organization affiliation.
 * @async
 * @function updateAffiliation
 * @param {number} affiliationId - The ID of the affiliation to update.
 * @param {Object} affiliationData - The affiliation data to update.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Affiliation update result or error.
 */
export async function updateAffiliation(affiliationId, affiliationData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/affiliations/${affiliationId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(affiliationData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update affiliation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Deletes a user/organization affiliation.
 * @async
 * @function deleteAffiliation
 * @param {number} userId - The ID of the user.
 * @param {number} orgId - The ID of the organization.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Affiliation deletion result or error.
 */
export async function deleteAffiliation(userId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/affiliations/delete/?user_id=${userId}&org_id=${orgId}`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete affiliation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Responds to an affiliation invite (accept, reject, or leave).
 * @function respondAffiliation
 * @param {Object} affiliationData - The affiliation response data.
 * @param {number} affiliationData.affiliation_id - The ID of the affiliation.
 * @param {number} affiliationData.user_id - The ID of the user responding.
 * @param {string} affiliationData.status - The new status ('active', 'rejected', or 'left').
 * @param {string} baseUrl - Optional base URL for the API.
 * @returns {Promise<Object>} Affiliation response result or error.
 */
export async function respondAffiliation(affiliationData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/affiliations/respond/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(affiliationData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to respond to affiliation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function addOrganizationAdmin(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/admins/add/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to add admin" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeOrganizationAdmin(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/admins/remove/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove admin" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function addOrganizationMember(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/members/add/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to add member" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeOrganizationMember(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/members/remove/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove member" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function addOrganizationCongregant(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/congregants/add/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to add congregant" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeOrganizationCongregant(orgId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/congregants/remove/${userId}/`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove congregant" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// User Connection

/**
 * Creates a new user-to-user connection.
 * @async
 * @function createConnection
 * @param {Object} connectionData - The connection data.
 * @param {number} connectionData.sender_id - The ID of the user sending the connection request.
 * @param {number} connectionData.receiver_id - The ID of the user receiving the connection request.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Connection creation result or error.
 */
export async function createConnection(connectionData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/connections/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(connectionData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create connection" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Updates an existing user-to-user connection.
 * @async
 * @function updateConnection
 * @param {number} connectionId - The ID of the connection to update.
 * @param {Object} connectionData - The connection data to update.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Connection update result or error.
 */
export async function updateConnection(connectionId, connectionData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/connections/${connectionId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(connectionData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update connection" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Deletes a user-to-user connection.
 * @async
 * @function deleteConnection
 * @param {number} userId1 - The ID of the first user (order-independent).
 * @param {number} userId2 - The ID of the second user (order-independent).
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Connection deletion result or error.
 */
export async function deleteConnection(userId1, userId2, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/connections/delete/?user_id1=${userId1}&user_id2=${userId2}`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete connection" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Responds to a connection invite (accept, reject, or leave).
 * @function respondConnection
 * @param {Object} responseData - The connection response data.
 * @param {number} responseData.connection_id - The ID of the connection.
 * @param {number} responseData.user_id - The ID of the user responding.
 * @param {string} responseData.status - The new status ('active', 'rejected', or 'left').
 * @param {string} baseUrl - Optional base URL for the API.
 * @returns {Promise<Object>} Connection response result or error.
 */
export async function respondConnection(responseData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/connections/respond/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(responseData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to respond to connection" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets all connections for a user.
 * @async
 * @function getUserConnections
 * @param {number} userId - The ID of the user.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Connections data or error.
 */
export async function getUserConnections(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/connections/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get user connections" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Follow/Unfollow

export async function follow(followerId, followeeId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/follow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ follower_id: followerId, followee_id: followeeId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to follow" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function unfollow(followerId, followeeId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/unfollow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ follower_id: followerId, followee_id: followeeId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to unfollow" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Posts

export async function createPost(postData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/posts/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(postData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function deletePost(postId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/posts/${postId}/delete/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function updatePost(postId, postData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/posts/${postId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(postData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets a post by id, optionally as a specific viewer (likes/connection state).
 * @async
 * @param {number} postId
 * @param {number|string} [viewerId] - Viewer user id, or a base URL (legacy tests)
 * @param {string} [baseUrl]
 * @returns {Promise<Object>} Post or error
 */
export async function getPost(postId, viewerId, baseUrl = API_BASE_URL) {
  let resolvedViewerId = viewerId;
  let resolvedBaseUrl = baseUrl;
  if (typeof viewerId === "string") {
    resolvedBaseUrl = viewerId;
    resolvedViewerId = undefined;
  }
  try {
    const params = new URLSearchParams();
    if (resolvedViewerId != null && resolvedViewerId !== "") {
      params.set("viewer_id", String(resolvedViewerId));
    }
    const query = params.toString();
    const res = await apiFetch(
      `${resolvedBaseUrl}/api/posts/${postId}/${query ? `?${query}` : ""}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Events

export async function createEvent(eventData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/events/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create event" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function deleteEvent(eventId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/events/${eventId}/delete/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete event" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function updateEvent(eventId, eventData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/events/${eventId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update event" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getEvent(eventId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/events/${eventId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get event" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Events Going/Interested

function reactionQuery(isOrg = false) {
  return isOrg ? "?is_org=true" : "";
}

/** Support legacy (id, id, baseUrl) and new (id, id, isOrg, baseUrl) call shapes. */
function resolveReactionArgs(isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  if (typeof isOrgOrBaseUrl === "string") {
    return { isOrg: false, baseUrl: isOrgOrBaseUrl };
  }
  return { isOrg: !!isOrgOrBaseUrl, baseUrl };
}

export async function goingEvent(eventId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/events/${eventId}/going/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to mark going" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeGoingEvent(eventId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/events/${eventId}/going/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove going" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function interestedEvent(eventId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/events/${eventId}/interested/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to mark interested" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeInterestedEvent(eventId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/events/${eventId}/interested/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove interested" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Post/Event Comments

export async function createComment(commentData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/comments/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(commentData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function deleteComment(commentId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/comments/${commentId}/delete/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function updateComment(commentId, commentData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/comments/${commentId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(commentData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getComment(commentId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/comments/${commentId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Posts Like/Dislike

export async function likePost(postId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/posts/${postId}/like/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to like post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeLikePost(postId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/posts/${postId}/like/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove like" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function dislikePost(postId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/posts/${postId}/dislike/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to dislike post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeDislikePost(postId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/posts/${postId}/dislike/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove dislike" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Comment Like/Dislike
export async function likeComment(commentId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/comments/${commentId}/like/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to like comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeLikeComment(commentId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/comments/${commentId}/like/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove like" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function dislikeComment(commentId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/comments/${commentId}/dislike/${userId}/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to dislike comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function removeDislikeComment(commentId, userId, isOrgOrBaseUrl = false, baseUrl = API_BASE_URL) {
  try {
    const { isOrg, baseUrl: url } = resolveReactionArgs(isOrgOrBaseUrl, baseUrl);
    const res = await apiFetch(`${url}/api/comments/${commentId}/dislike/${userId}/remove/${reactionQuery(isOrg)}`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove dislike" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Post/Event Feed

/**
 * Gets comments for a post to be shown to the given user.
 * @async
 * @function getUserPostComments
 * @param {number} userId - The user ID.
 * @param {number} postId - The post ID.
 * @param {number} [offset=0] - Pagination offset.
 * @param {number} [limit=10] - Pagination limit.
 * @returns {Promise<Object>} Comments or error.
 */
export async function getUserPostComments(userId, postId, offset, limit, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/posts/${postId}/comments/?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get comments" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets replies to a comment to be shown to the given user.
 * @async
 * @function getUserCommentReplies
 * @param {number} userId - The user ID.
 * @param {number} commentId - The comment ID.
 * @param {number} [offset=0] - Pagination offset.
 * @param {number} [limit=10] - Pagination limit.
 * @returns {Promise<Object>} Replies or error.
 */
export async function getUserCommentReplies(userId, commentId, offset, limit, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/comments/${commentId}/replies/?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get replies" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getUserFeed(userId, offset = 0, limit = 10, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/feed/?offset=${offset}&limit=${limit}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get user feed" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Search

export async function search(userId, query, includePosts = true, includeEvents = true,
                             includeOrganizations = true, includeUsers = true, baseUrl = API_BASE_URL,
                             latitude = null, longitude = null, radius = null, bbox = null) {
  try {
    const params = new URLSearchParams({
      query: query || "",
      includePosts: String(includePosts),
      includeEvents: String(includeEvents),
      includeOrganizations: String(includeOrganizations),
      includeUsers: String(includeUsers),
    });
    if (latitude !== null && latitude !== undefined) {
      params.set("latitude", String(latitude));
    }
    if (longitude !== null && longitude !== undefined) {
      params.set("longitude", String(longitude));
    }
    if (radius !== null && radius !== undefined) {
      params.set("radius", String(radius));
    }
    if (bbox && Number.isFinite(bbox.west) && Number.isFinite(bbox.south)
        && Number.isFinite(bbox.east) && Number.isFinite(bbox.north)) {
      params.set("minLat", String(bbox.south));
      params.set("maxLat", String(bbox.north));
      params.set("minLon", String(bbox.west));
      params.set("maxLon", String(bbox.east));
    }
    const res = await apiFetch(`${baseUrl}/api/search/${userId}/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Geocode a city or address via the backend Nominatim proxy.
 * @async
 * @param {string} query
 * @param {"city"|"address"} [searchType="city"]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>} `{ success, data }` or `{ error }`.
 */
export async function searchLocation(query, searchType = "city", baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams({
      q: query,
      searchType,
    });
    const res = await apiFetch(`${baseUrl}/api/search/location/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search location" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function searchUsers(userId, query, baseUrl = API_BASE_URL) {
  return search(userId, query, false, false, false, true, baseUrl);
}

/**
 * Search users and organizations for @mentions.
 * @param {number} userId
 * @param {string} query
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function searchMentionTargets(userId, query, baseUrl = API_BASE_URL) {
  return search(userId, query, false, false, true, true, baseUrl);
}

/**
 * Connections and affiliated orgs (or org affiliates) for the empty @ picker.
 * @param {number} userId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getMentionCandidates(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/mention-candidates/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get mention candidates" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Mentions of the current account, newest first.
 * @param {number} userId
 * @param {{ offset?: number, limit?: number }} [options]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getUserMentions(userId, options = {}, baseUrl = API_BASE_URL) {
  try {
    const offset = options.offset ?? 0;
    const limit = options.limit ?? 20;
    const res = await apiFetch(
      `${baseUrl}/api/users/${userId}/mentions/?offset=${offset}&limit=${limit}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get mentions" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function getSuggestedUsers(userId, limit = 8, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/suggestions/?limit=${limit}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to fetch suggestions" };
    return data;
  } catch (err) {
    return { error: "Network error" };
  }
}

/**
 * Random users and organizations with coordinates for idle map display (explore + landing).
 * @param {number} userId - Requesting user (computed fields); may be 0 when unauthenticated.
 * @param {number} [limit=15] - Total items; server clamps (typically 10–20), split roughly half users / half orgs.
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getExploreIdleUsers(userId, limit = 15, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/users/${userId}/explore/idle/?limit=${encodeURIComponent(String(limit))}`
    );
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || "Failed to load explore idle users" };
    return data;
  } catch (err) {
    return { success: false, error: "Network error or server unavailable" };
  }
}

export async function searchOrganizations(query, baseUrl = "") {
  try {
    const res = await apiFetch(`${baseUrl}/api/search/organizations/?query=${encodeURIComponent(query)}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search organizations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Searches for events based on the provided query.
 * @async
 * @function searchEvents
 * @param {string} query - The search query.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Search results or error.
 */
export async function searchEvents(query, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/search/events/?query=${encodeURIComponent(query)}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search events" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

// Messaging API Functions

/**
 * Gets all chats for a user.
 * @async
 * @function getChats
 * @param {number} userId - The ID of the user.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Chats data or error.
 */
export async function getChats(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/chats/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get chats" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets organization-scoped channels for a viewer (affiliate or org account).
 * @async
 * @param {number} orgId
 * @param {number} viewerId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getOrganizationChats(orgId, viewerId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/organizations/${orgId}/chats/${viewerId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get organization chats" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Search personal chats by name and messages by content or author name.
 * @async
 * @param {number} userId
 * @param {string} query
 * @param {string} [baseUrl]
 * @returns {Promise<Object>} `{ success, data: { chats, messages } }` or error.
 */
export async function searchUserInbox(userId, query, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams();
    params.set("query", query);
    const res = await apiFetch(
      `${baseUrl}/api/users/${userId}/chats/search/?${params.toString()}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search messages" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Search organization channels by name and messages by content or author name.
 * @async
 * @param {number} orgId
 * @param {number} viewerId
 * @param {string} query
 * @param {string} [baseUrl]
 * @returns {Promise<Object>} `{ success, data: { chats, messages } }` or error.
 */
export async function searchOrgInbox(orgId, viewerId, query, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams();
    params.set("query", query);
    const res = await apiFetch(
      `${baseUrl}/api/organizations/${orgId}/chats/${viewerId}/search/?${params.toString()}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to search channels" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Creates or finds a DM chat between two users.
 * @async
 * @function createDM
 * @param {number} userId - The ID of the first user.
 * @param {number} otherUserId - The ID of the second user.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Chat data or error.
 */
export async function createDM(userId, otherUserId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/dm/create/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, other_user_id: otherUserId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create DM" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets messages for a specific chat (cursor pagination, newest page first).
 * @async
 * @function getChatMessages
 * @param {number} chatId - The ID of the chat.
 * @param {number} userId - The ID of the user requesting messages.
 * @param {Object} [options] - Pagination options.
 * @param {string} [options.beforeCreatedAt] - Cursor timestamp (with beforeId) for older page.
 * @param {number} [options.beforeId] - Cursor message id (with beforeCreatedAt).
 * @param {number} [options.limit=20] - Max messages to return.
 * @param {number} [options.aroundId] - Center the page on this message id.
 * @param {string} [baseUrl] - The base URL for the API.
 * @returns {Promise<Object>} Wrapped page `{ messages, has_more, next_cursor }` or error.
 */
export async function getChatMessages(chatId, userId, options = {}, baseUrl = API_BASE_URL) {
  try {
    const { beforeCreatedAt, beforeId, aroundId, limit = 20 } = options || {};
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (aroundId != null && aroundId !== "") {
      params.set("around_id", String(aroundId));
    } else if (beforeCreatedAt != null && beforeCreatedAt !== "" && beforeId != null) {
      params.set("before_created_at", beforeCreatedAt);
      params.set("before_id", String(beforeId));
    }
    const res = await apiFetch(
      `${baseUrl}/api/chats/${chatId}/messages/${userId}/?${params.toString()}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get chat messages" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Creates a new chat (DM or group).
 * @async
 * @function createChat
 * @param {Object} chatData - The data for the chat.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Chat creation result or error.
 */
export async function createChat(chatData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(chatData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create chat" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Updates a chat.
 * @async
 * @function updateChat
 * @param {number} chatId - The ID of the chat to update.
 * @param {Object} chatData - The data to update.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Chat update result or error.
 */
export async function updateChat(chatId, chatData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(chatData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update chat" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Deletes a chat.
 * @async
 * @function deleteChat
 * @param {number} chatId - The ID of the chat to delete.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Chat deletion result or error.
 */
export async function deleteChat(chatId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/delete/`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete chat" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}


/**
 * Adds a member to a chat.
 * @async
 * @function createChatMember
 * @param {number} chatId - The ID of the chat.
 * @param {Object} memberData - The member data.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Member addition result or error.
 */
export async function createChatMember(chatId, memberData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/members/add/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(memberData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to add chat member" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Updates a chat member.
 * @async
 * @function updateChatMember
 * @param {number} chatId - The ID of the chat.
 * @param {number} memberId - The ID of the member to update.
 * @param {Object} memberData - The member data to update.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Member update result or error.
 */
export async function updateChatMember(chatId, memberId, memberData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/members/${memberId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(memberData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update chat member" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Removes a member from a chat.
 * @async
 * @function deleteChatMember
 * @param {number} chatId - The ID of the chat.
 * @param {number} memberId - The ID of the member to remove.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Member removal result or error.
 */
export async function deleteChatMember(chatId, memberId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/members/${memberId}/remove/`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove chat member" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Creates a new message in a chat.
 * @async
 * @function createChatMessage
 * @param {number} chatId - The ID of the chat.
 * @param {Object} messageData - The message data.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Message creation result or error.
 */
export async function createChatMessage(chatId, messageData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/messages/create/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(messageData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to create chat message" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Updates a chat message.
 * @async
 * @function updateChatMessage
 * @param {number} messageId - The ID of the message to update.
 * @param {Object} messageData - The message data to update.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Message update result or error.
 */
export async function updateChatMessage(messageId, messageData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/messages/${messageId}/update/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(messageData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update chat message" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Deletes a message from a chat.
 * @async
 * @function deleteChatMessage
 * @param {number} messageId - The ID of the message to delete.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Message deletion result or error.
 */
export async function deleteChatMessage(messageId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/messages/${messageId}/delete/`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete chat message" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Marks a message as read by a user.
 * @async
 * @function createChatRead
 * @param {Object} readData - The read data containing message_id and user_id.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Message read result or error.
 */
export async function createChatRead(readData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/messages/read/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(readData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to read chat message" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Marks all unread messages in a chat as read by a user.
 * @async
 * @function readAllChatMessages
 * @param {number} chatId - The ID of the chat.
 * @param {number} userId - The ID of the user marking messages as read.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Result with count of messages marked as read, or error.
 */
export async function readAllChatMessages(chatId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/chats/${chatId}/messages/read_all/${userId}/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to mark messages as read" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Reacts to a message in a chat.
 * @async
 * @function createChatReaction
 * @param {Object} reactionData - The reaction data containing message_id, user_id, and reaction.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Reaction result or error.
 */
export async function createChatReaction(reactionData, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/messages/reactions/add/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reactionData),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to react to chat message" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Removes a reaction from a message in a chat.
 * @async
 * @function deleteChatReaction
 * @param {number} reactionId - The ID of the reaction to remove.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Reaction removal result or error.
 */
export async function deleteChatReaction(reactionId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/reactions/${reactionId}/remove/`, {
      method: "POST",
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to remove chat message reaction" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Submits user feedback to the backend.
 * @async
 * @function createUserFeedback
 * @param {string} firstName - The user's first name.
 * @param {string} lastName - The user's last name.
 * @param {string} email - The user's email address.
 * @param {string} feedback - The feedback message.
 * @param {string} baseUrl - The base URL for the API.
 * @returns {Promise<Object>} Feedback submission result or error.
 */
export async function createUserFeedback(firstName, lastName, email, feedback, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/feedback/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        first_name: firstName,
        last_name: lastName,
        email: email,
        feedback: feedback,
      }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to submit feedback" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets account settings.
 * @async
 * @param {number} accountId
 * @param {string|{ accountType?: string, baseUrl?: string }} [baseUrlOrOptions]
 * @returns {Promise<Object>}
 */
export async function getSettings(accountId, baseUrlOrOptions = API_BASE_URL) {
  const isOptions = baseUrlOrOptions && typeof baseUrlOrOptions === "object";
  const baseUrl = isOptions ? (baseUrlOrOptions.baseUrl || API_BASE_URL) : baseUrlOrOptions;
  const accountType = isOptions ? (baseUrlOrOptions.accountType || "user") : "user";
  const path =
    accountType === "organization"
      ? `${baseUrl}/api/organizations/${accountId}/settings/`
      : `${baseUrl}/api/users/${accountId}/settings/`;
  try {
    const res = await apiFetch(path);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load settings" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Merges a partial settings patch onto the account (autosave).
 * @async
 * @param {number} accountId
 * @param {Object} patch
 * @param {string|{ accountType?: string, baseUrl?: string }} [baseUrlOrOptions]
 * @returns {Promise<Object>}
 */
export async function updateSettings(accountId, patch, baseUrlOrOptions = API_BASE_URL) {
  const isOptions = baseUrlOrOptions && typeof baseUrlOrOptions === "object";
  const baseUrl = isOptions ? (baseUrlOrOptions.baseUrl || API_BASE_URL) : baseUrlOrOptions;
  const accountType = isOptions ? (baseUrlOrOptions.accountType || "user") : "user";
  const path =
    accountType === "organization"
      ? `${baseUrl}/api/organizations/${accountId}/settings/`
      : `${baseUrl}/api/users/${accountId}/settings/`;
  try {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update settings" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Gets platform admin profile information.
 * @async
 * @param {number} adminId
 * @param {number} targetAdminId
 * @returns {Promise<Object>}
 */
export async function getAdmin(adminId, targetAdminId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/info/${targetAdminId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get admin profile" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Lists organizations pending platform verification.
 * @async
 * @param {number} adminId
 * @returns {Promise<Object>}
 */
export async function getPendingVerificationOrganizations(adminId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/organizations/pending/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get pending organizations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List personal accounts pending platform verification.
 * @async
 * @param {number} adminId
 * @returns {Promise<Object>}
 */
export async function getPendingVerificationUsers(adminId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/users/pending/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to get pending users" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Sets pending organization verification decision.
 * @async
 * @param {number} adminId
 * @param {number} orgId
 * @param {"approve"|"reject"} decision
 * @returns {Promise<Object>}
 */
export async function setOrganizationVerificationStatus(
  adminId,
  orgId,
  decision,
  baseUrl = API_BASE_URL
) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/organizations/${orgId}/verification/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update organization verification" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Approve or reject a pending personal account.
 * @async
 * @param {number} adminId
 * @param {number} userId
 * @param {"approve"|"reject"} decision
 * @returns {Promise<Object>}
 */
export async function setUserVerificationStatus(
  adminId,
  userId,
  decision,
  baseUrl = API_BASE_URL
) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/users/${userId}/verification/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision }),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update user verification" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Platform-admin search over user accounts.
 * @async
 * @param {number} adminId
 * @param {string} query
 * @returns {Promise<Object>}
 */
export async function adminSearchUsers(adminId, query, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams();
    if (query !== undefined && query !== null) params.set("query", query);
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/users/search/?${params.toString()}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "User search failed", ...data };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Platform-admin search over organization accounts.
 * @async
 * @param {number} adminId
 * @param {string} query
 * @returns {Promise<Object>}
 */
export async function adminSearchOrganizations(adminId, query, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams();
    if (query !== undefined && query !== null) params.set("query", query);
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/organizations/search/?${params.toString()}`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Organization search failed", ...data };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Hard-delete a user account (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} userId
 * @returns {Promise<Object>}
 */
export async function adminDeleteUser(adminId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/users/${userId}/delete/`,
      { method: "DELETE" }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete user" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Hard-delete an organization account (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} orgId
 * @returns {Promise<Object>}
 */
export async function adminDeleteOrganization(adminId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/organizations/${orgId}/delete/`,
      { method: "DELETE" }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load a user profile for platform-admin review.
 * @async
 * @param {number} adminId
 * @param {number} userId
 * @returns {Promise<Object>}
 */
export async function adminGetUser(adminId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/users/${userId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load user" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load an organization profile for platform-admin review.
 * @async
 * @param {number} adminId
 * @param {number} orgId
 * @returns {Promise<Object>}
 */
export async function adminGetOrganization(adminId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/organizations/${orgId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load organization" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List posts authored by a user (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} userId
 * @returns {Promise<Object>}
 */
export async function adminGetUserPosts(adminId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/users/${userId}/posts/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load posts" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List posts authored by an organization (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} orgId
 * @returns {Promise<Object>}
 */
export async function adminGetOrganizationPosts(adminId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/organizations/${orgId}/posts/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load posts" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List comments and replies authored by a user (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} userId
 * @returns {Promise<Object>}
 */
export async function adminGetUserComments(adminId, userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/users/${userId}/comments/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load comments" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List comments and replies authored by an organization (platform admin).
 * @async
 * @param {number} adminId
 * @param {number} orgId
 * @returns {Promise<Object>}
 */
export async function adminGetOrganizationComments(adminId, orgId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/organizations/${orgId}/comments/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load comments" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load a post for platform-admin review.
 * @async
 * @param {number} adminId
 * @param {number} postId
 * @returns {Promise<Object>}
 */
export async function adminGetPost(adminId, postId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/posts/${postId}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Soft-delete a post as a platform admin, optionally emailing the author.
 * @async
 * @param {number} adminId
 * @param {number} postId
 * @param {{notify?: boolean, moderatorComment?: string}} [options]
 * @returns {Promise<Object>}
 */
export async function adminDeletePost(
  adminId,
  postId,
  { notify = true, moderatorComment = "" } = {},
  baseUrl = API_BASE_URL
) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/posts/${postId}/delete/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        notify,
        moderator_comment: moderatorComment,
      }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete post" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Soft-delete a comment or reply as a platform admin, optionally emailing the author.
 * @async
 * @param {number} adminId
 * @param {number} commentId
 * @param {{notify?: boolean, moderatorComment?: string}} [options]
 * @returns {Promise<Object>}
 */
export async function adminDeleteComment(
  adminId,
  commentId,
  { notify = true, moderatorComment = "" } = {},
  baseUrl = API_BASE_URL
) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/comments/${commentId}/delete/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        notify,
        moderator_comment: moderatorComment,
      }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to delete comment" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Send or refresh a platform org signup invitation email (links to /new-org).
 * @async
 * @param {number} adminId
 * @param {{ email: string, invitee_name: string }} payload
 * @returns {Promise<Object>}
 */
export async function sendPlatformOrgInvitation(adminId, payload, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/org-invitations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to send invitation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Lists outreach invitations still pending (no user or org account for that email).
 * @async
 * @param {number} adminId
 * @returns {Promise<Object>}
 */
export async function listPendingPlatformOrgInvitations(adminId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/org-invitations/pending/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load pending invitations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Resends the platform org invitation email for a pending row.
 * @async
 * @param {number} adminId
 * @param {number} invitationId
 * @returns {Promise<Object>}
 */
export async function resendPlatformOrgInvitation(adminId, invitationId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/org-invitations/${invitationId}/resend/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to resend invitation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Send or refresh an individual join invitation.
 * @async
 * @param {number} adminId
 * @param {{ first_name: string, last_name: string, email: string, pre_approved?: boolean }} payload
 * @returns {Promise<Object>}
 */
export async function sendPlatformUserInvitation(adminId, payload, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/user-invitations/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to send invitation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List individual join invitations.
 * @async
 * @param {number} adminId
 * @returns {Promise<Object>}
 */
export async function listPlatformUserInvitations(adminId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/user-invitations/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load invitations" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Rotate the token and resend an individual invitation.
 * @async
 * @param {number} adminId
 * @param {number} invitationId
 * @returns {Promise<Object>}
 */
export async function resendPlatformUserInvitation(adminId, invitationId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/user-invitations/${invitationId}/resend/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to resend invitation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Revoke an unclaimed individual invitation.
 * @async
 * @param {number} adminId
 * @param {number} invitationId
 * @returns {Promise<Object>}
 */
export async function revokePlatformUserInvitation(adminId, invitationId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/user-invitations/${invitationId}/revoke/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to revoke invitation" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load the name and suggested username for a join link.
 * @async
 * @param {string} token
 * @returns {Promise<Object>}
 */
export async function validateJoinInvitation(token, baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams({ token });
    const res = await apiFetch(`${baseUrl}/api/join/validate/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "This invitation is invalid or expired." };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Claim an individual invitation and start a session.
 * @async
 * @param {{ token: string, first_name: string, last_name: string, username: string, terms_accepted: boolean }} payload
 * @returns {Promise<Object>}
 */
/**
 * Exchange a welcome-email token for a session.
 * @async
 * @param {string} token
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function redeemProfileSetup(token, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/profile-setup/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "This link is invalid or expired." };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function claimJoinInvitation(payload, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/join/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Could not create the account" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List survey catalog plus this user's progress.
 * @async
 * @param {number} userId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function listUserSurveys(userId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/surveys/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to list surveys" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load one survey definition plus this user's draft and submissions.
 * @async
 * @param {number} userId
 * @param {string} surveyId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getUserSurvey(userId, surveyId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/surveys/${encodeURIComponent(surveyId)}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load survey" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Create or update an in-progress survey draft.
 * @async
 * @param {number} userId
 * @param {string} surveyId
 * @param {Object} answers
 * @param {string} [currentPageId]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function saveSurveyDraft(userId, surveyId, answers, currentPageId = "", baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/surveys/${encodeURIComponent(surveyId)}/draft/`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers, current_page_id: currentPageId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to save draft" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Submit a completed survey attempt.
 * @async
 * @param {number} userId
 * @param {string} surveyId
 * @param {Object} answers
 * @param {string} [currentPageId]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function submitSurvey(userId, surveyId, answers, currentPageId = "", baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/users/${userId}/surveys/${encodeURIComponent(surveyId)}/submit/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers, current_page_id: currentPageId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to submit survey" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Patch answers on a submitted one-shot survey (allowEdit).
 * @async
 * @param {number} userId
 * @param {string} surveyId
 * @param {Object} answers
 * @param {string} [currentPageId]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function saveSubmittedSurvey(userId, surveyId, answers, currentPageId = "", baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/users/${userId}/surveys/${encodeURIComponent(surveyId)}/submitted/`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, current_page_id: currentPageId }),
      }
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to update answers" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load a public (unauthenticated) survey definition.
 * @async
 * @param {string} surveyId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getPublicSurvey(surveyId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/public-surveys/${encodeURIComponent(surveyId)}/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load survey" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Submit a public survey without a signed-in user.
 * @async
 * @param {string} surveyId
 * @param {Object} answers
 * @param {string} [currentPageId]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function submitPublicSurvey(surveyId, answers, currentPageId = "", baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/public-surveys/${encodeURIComponent(surveyId)}/submit/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers, current_page_id: currentPageId }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to submit survey" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List survey definitions for platform admin.
 * @async
 * @param {number} adminId
 * @param {string} [audience]
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function listAdminSurveys(adminId, audience = "authenticated", baseUrl = API_BASE_URL) {
  try {
    const params = new URLSearchParams({ audience });
    const res = await apiFetch(`${baseUrl}/api/admins/${adminId}/surveys/?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to list surveys" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * List submitted survey responses for platform admin.
 * @async
 * @param {number} adminId
 * @param {string} surveyId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function listAdminSurveyResponses(adminId, surveyId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/surveys/${encodeURIComponent(surveyId)}/responses/`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to list responses" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Load one submitted survey response for platform admin.
 * @async
 * @param {number} adminId
 * @param {string} surveyId
 * @param {number} responseId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function getAdminSurveyResponse(adminId, surveyId, responseId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/surveys/${encodeURIComponent(surveyId)}/responses/${responseId}/`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to load response" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/**
 * Download all submitted responses for a survey (JSON export payload).
 * @async
 * @param {number} adminId
 * @param {string} surveyId
 * @param {string} [baseUrl]
 * @returns {Promise<Object>}
 */
export async function exportAdminSurveyResponses(adminId, surveyId, baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(
      `${baseUrl}/api/admins/${adminId}/surveys/${encodeURIComponent(surveyId)}/export/`
    );
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to export responses" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

/* TESTING */
export async function clear(baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/testing/clear/`, { method: "DELETE" });
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to clear" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}

export async function isEmpty(baseUrl = API_BASE_URL) {
  try {
    const res = await apiFetch(`${baseUrl}/api/testing/is_empty/`);
    const data = await res.json();
    if (!res.ok) return { error: data.error || "Failed to check if database is empty" };
    return data;
  } catch (err) {
    return { error: "Network error or server unavailable" };
  }
}
